import { RenderedSpecialCondition } from '@urbanx/agx.campaigns.client'
import {
  AgxBadge,
  AgxButton,
  AgxCollapsible,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import SpecialConditionClause from './components/SpecialConditionClause'
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation'

interface SpecialConditionCollapsiblePanelProps {
  contractId: string
  campaignId: string
  renderedSpecialCondition: RenderedSpecialCondition
  onDeleteCondition: (conditionId: string) => void
  skipDeleteConfirmation?: boolean
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDate: Date | undefined
  ) => void
}

const SpecialConditionCollapsiblePanel = ({
  renderedSpecialCondition,
  onDeleteCondition,
  skipDeleteConfirmation = false,
  contractId,
  campaignId,
  onDueDateUpdated,
}: SpecialConditionCollapsiblePanelProps) => {
  const title = useMemo(
    () =>
      `${renderedSpecialCondition.position} ${renderedSpecialCondition.title}`,
    [renderedSpecialCondition]
  )

  const furthestDueDate = useMemo(() => {
    const allDates = renderedSpecialCondition.clauses
      ?.map((clause) => clause.dueDate)
      .filter((date) => date !== undefined)

    if (!allDates || allDates.length === 0) return undefined

    const validDates = allDates
      .map((date) => dayjs(date))
      .filter((date) => date.isValid())

    if (validDates.length === 0) return undefined

    const earliestDate = validDates.reduce((earliest, current) =>
      current.isBefore(earliest) ? current : earliest
    )

    const day = earliestDate.date()
    const month = earliestDate.format('MMM')
    const suffix =
      day % 100 >= 11 && day % 100 <= 13
        ? 'th'
        : day % 10 === 1
          ? 'st'
          : day % 10 === 2
            ? 'nd'
            : day % 10 === 3
              ? 'rd'
              : 'th'

    return `Due ${day}${suffix} ${month}`
  }, [renderedSpecialCondition])

  const onConditionDeleted = useCallback(() => {
    if (!renderedSpecialCondition?.id) return

    onDeleteCondition(renderedSpecialCondition.id)
  }, [renderedSpecialCondition?.id, onDeleteCondition])

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  return (
    <AgxCollapsible
      key={renderedSpecialCondition.id}
      enableToggle
      open
      customTitle={
        <AgxSpacer
          fill
          direction="horizontal"
          gap="xs"
          contentAlignment="center"
        >
          <AgxTypography
            id={`special-condition-${renderedSpecialCondition.id}`}
            type={'title'}
            size={'large'}
            color="var(--text-primary)"
          >
            {title}
          </AgxTypography>
          {furthestDueDate !== undefined && (
            <AgxBadge
              type="colourfill"
              colour="yellow"
              size="md"
              text={furthestDueDate}
            />
          )}
        </AgxSpacer>
      }
      variant={'withDivider'}
      content={
        <AgxSpacer direction="vertical" gap={'md'}>
          {renderedSpecialCondition.clauses?.map((clause, index) => (
            <SpecialConditionClause
              clause={clause}
              key={clause.clauseId}
              isLastClause={
                index === (renderedSpecialCondition.clauses?.length ?? 0) - 1
              }
              conditionId={renderedSpecialCondition.id ?? ''}
              contractId={contractId}
              campaignId={campaignId}
              onDueDateUpdated={onDueDateUpdated}
            />
          ))}

          <AgxSpacer direction="horizontal" fill contentJustification="end">
            <AgxButton
              id={`delete-${renderedSpecialCondition.id}`}
              variant="link"
              danger
              text="Delete"
              onClick={() =>
                skipDeleteConfirmation
                  ? onConditionDeleted()
                  : setShowDeleteConfirmation(true)
              }
            />
            {!skipDeleteConfirmation && (
              <DeleteConfirmation
                name={`${renderedSpecialCondition.title} and all its clauses`}
                title="Really delete special condition?"
                lineOne="Are you sure you want to delete special condition"
                lineTwo="This cannot be undone."
                primaryActionTitle="Permanently delete special condition"
                secondaryActionTitle="No, cancel"
                showPopUp={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={onConditionDeleted}
              />
            )}
          </AgxSpacer>
        </AgxSpacer>
      }
    />
  )
}

export default SpecialConditionCollapsiblePanel
